import React, {
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
  useRef,
  FC,
  useEffect,
  useContext,
  Key,
} from 'react';
import debounce from 'lodash/debounce';
import AutoCompletedInput from 'core/components/AutoComplete';
import {
  maxBillingCodeSearchQueryLength,
} from 'core/constants';
import Spinner from 'pages/Dashboard/components/Spinner';
import defer from 'lodash/defer';
import compact from 'lodash/compact';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  ServiceCode,
  ServiceType,
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import BillingCodeRow from 'views/EMR/components/ServiceLookup/BillingCodeRow';
import CustomServiceRow from 'views/EMR/components/ServiceLookup/CustomServiceRow';
import {
  SelectOptionProps,
} from 'core/components/Lookup/Generic/DefaultOption';
import {
  useGetServiceCodes,
} from 'views/EMR/components/ServiceLookup/hooks';
import {
  ServiceContext,
} from 'views/EMR/components/ServiceLookup/ServiceContext';
import ListboxDropdown from 'views/EMR/components/ListboxDropdown';
import {
  getErrorMessage,
} from 'utils/errors';
import {
 isEmptyString,
} from '@populateui/utils';

const RowByType: Record<ServiceType, FC<SelectOptionProps<ServiceCode>>> = {
  CPTCode: BillingCodeRow,
  CustomCode: CustomServiceRow,
  ICDCode: BillingCodeRow,
};

export default function Lookup() {
  const {
    id,
    placeholder,
    value: selectedServices = [],
    normalizeOption: makeNew,
    onChange,
    favorites = [],
    refetchFavoriteCode,
    dropdownId,
    type = 'CustomCode',
    getKey,
    allowDuplicate = false,
    externalFilter,
    showFavorites,
  } = useContext(ServiceContext);

  const [searchValue, setSearchValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);

  const fetchNextPage = useCallback(() => {
    setPage(page + 1);
  }, [page, setPage]);

  const inputRef = useRef<HTMLInputElement>(null);
  const {
    data,
    error,
    refetch: refetchCodes,
    isError,
    isFetching,
  } = useGetServiceCodes(
    type,
    searchValue,
    0,
    (page + 1) * 10,
  );

  useEffect(() => {
    if (page > 0) {
      refetchCodes();
    }
  }, [page]);

  useEffect(() => {
    if (showFavorites) {
      setOpen(true);
      inputRef?.current?.focus();
    }
  }, [showFavorites]);

  const { data: _options, pageProperties } = data ?? {};

  const options = useMemo(
    () => (showFavorites ? favorites : _options) ?? [],
    [showFavorites, favorites, _options],
  );

  const refetchAll = async () => {
    await Promise.all([
      refetchFavoriteCode?.(),
      refetchCodes?.(),
    ]);
  };

  const handleInputChange = useCallback(debounce((
    event: SyntheticEvent,
    newInputValue: string,
  ) => {
    if (open) {
      setSearchValue(newInputValue);
    }
  }, 700), [open]);

  const noOptionsText = useMemo(() => {
    if (isError) {
      return getErrorMessage(error) ?? 'Unable to load search results.';
    }

    if (isFetching) {
      return "Searching...";
    }

    return (options ?? []).length === 0 && isEmptyString(searchValue)
      ? 'Start typing to see the list..'
      : 'No result Found';
  }, [error, isError, options, searchValue]);

  const handleChange = (value: VisitNoteServiceCode[]) => {
    onChange(compact(value));
    visitNoteEventChannel.emit('onServiceCodesUpdate', type);
    setOpen(false);
  };

  const Row = RowByType[type];

  return (
    <AutoCompletedInput<VisitNoteServiceCode>
      multiple
      id={id}
      value={selectedServices as VisitNoteServiceCode[]}
      open={open}
      onOpen={() => { setOpen(true); }}
      onClose={() => {
        defer(() => {
          if (!document.activeElement?.closest(`#${dropdownId}`)) {
            setOpen(false);
          }
        });
      }}
      inputRef={inputRef}
      options={options ?? []}
      getLabel={({ serviceName, description }) => compact([serviceName, description]).join(' - ')}
      isOptionEqualToValue={(a, b) => (
        allowDuplicate ? false : (getKey(a) ?? '') === (getKey(b as VisitNoteServiceCode) ?? '')
      )}
      onChange={(value) => handleChange(value as VisitNoteServiceCode[])}
      renderTags={() => null}
      onInputChange={showFavorites ? undefined : handleInputChange}
      popupIcon={isFetching ? <Spinner /> : undefined}
      placeholder={placeholder}
      loading={isFetching}
      loadingText="Searching..."
      normalizeOption={makeNew}
      noOptionsText={noOptionsText}
      inputProps={{ maxLength: maxBillingCodeSearchQueryLength }}
      clearOnBlur={false}
      blurOnSelect={false}
      disableCloseOnSelect
      disableRemove
      filterOptions={showFavorites ? externalFilter : (x) => x}
      dropdownClassName="p-2"
      slotProps={{
        listbox: {
          component: ListboxDropdown,
          pageProperties: showFavorites ? null : {
            ...pageProperties,
          },
          fetchNextPage,
          isLoadingMore: isFetching,
          id: dropdownId,
          count: options?.length,
        },
      } as Object}
      renderOption={(props, option, state) => {
        const { key, ...rest } = props as typeof props & { key?: Key };

        return (
          <Row
            key={key}
            {...rest}
            getLabel={(option) => option?.serviceName ?? ''}
            option={option}
            state={state}
            onClick={() => {
              const toAdd = makeNew(option);
              handleChange([...(selectedServices as VisitNoteServiceCode[]), toAdd]);
            }}
            refetch={refetchAll}
          />
        );
      }}
    />
  );
}
